<template>
    <div class="content mk-aff-payout-detail">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-8 col-9">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.menu.affiliate_payouts") }}<template v-if="p"> · {{ p.code }}</template></h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div v-loading="loading" class="container-fluid mt--6">
            <el-alert v-if="schemaMissing" type="error" :closable="false" show-icon class="mb-3" :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>
            <el-alert v-if="stale" type="warning" :closable="false" show-icon class="mb-3" :title="$t('marketing.affiliate.err.stale')">
                <base-button size="sm" type="secondary" class="mt-1" @click="load">{{ $t("marketing.affiliate.reload") }}</base-button>
            </el-alert>

            <!-- unknown / unreadable round: empty state + back link (verifier B #4) -->
            <card v-if="!p && !loading && !schemaMissing" class="text-center py-4" data-testid="aff-payout-not-found">
                <p class="text-muted mb-3">{{ $t("marketing.affiliate.payout.not_found") }}</p>
                <router-link to="/marketing/affiliate/payouts" class="btn btn-sm btn-secondary">← {{ $t("marketing.affiliate.payout.back_to_list") }}</router-link>
            </card>

            <template v-if="p">
                <!-- header -->
                <card class="mb-3">
                    <div class="d-flex flex-wrap align-items-start justify-content-between" style="gap: 12px;">
                        <div>
                            <div class="d-flex align-items-center" style="gap: 8px;">
                                <span class="h2 mb-0">{{ p.code }}</span>
                                <el-tag :type="statusTag(p.status)" data-testid="aff-payout-detail-status">{{ tk("marketing.affiliate.payout.status_", p.status) }}</el-tag>
                            </div>
                            <div class="small text-muted mt-1">
                                {{ $t("marketing.affiliate.payout.cutoff") }} {{ dateOnly(p.cutoff_at) }} ·
                                {{ $t("marketing.affiliate.payout.paid_of") }} {{ p.paid_count }}/{{ p.item_count }} ·
                                WHT {{ p.wht_percent }}%
                            </div>
                            <div v-if="p.note" class="small mt-1">{{ p.note }}</div>
                        </div>
                        <div class="d-flex flex-wrap" style="gap: 8px;">
                            <template v-if="canWrite">
                                <base-button v-if="acts.submit" type="primary" size="sm" :loading="busy" data-testid="aff-payout-submit" @click="submit">{{ $t("marketing.affiliate.payout.submit") }}</base-button>
                                <base-button v-if="acts.cancel" type="danger" size="sm" :loading="busy" data-testid="aff-payout-cancel" @click="cancelRound">{{ $t("marketing.affiliate.payout.cancel") }}</base-button>
                            </template>
                            <template v-if="acts.csv">
                                <base-button type="secondary" size="sm" data-testid="aff-transfer-csv" @click="csv('transfer')"><i class="fa fa-file-csv"></i> {{ $t("marketing.affiliate.payout.transfer_csv") }}</base-button>
                                <base-button type="secondary" size="sm" data-testid="aff-wht-csv" @click="csv('wht')"><i class="fa fa-file-csv"></i> {{ $t("marketing.affiliate.payout.wht_csv") }}</base-button>
                            </template>
                        </div>
                    </div>
                    <div class="row mt-3">
                        <div class="col-md-4 col-12 mb-2"><div class="small text-muted">{{ $t("marketing.affiliate.payout.gross") }}</div><div class="h3 mb-0">฿{{ money(p.total_gross) }}</div></div>
                        <div class="col-md-4 col-6 mb-2"><div class="small text-muted">{{ $t("marketing.affiliate.payout.wht") }}</div><div class="h3 mb-0">฿{{ money(p.total_wht) }}</div></div>
                        <div class="col-md-4 col-6 mb-2"><div class="small text-muted">{{ $t("marketing.affiliate.payout.net") }}</div><div class="h3 mb-0 text-success">฿{{ money(p.total_net) }}</div></div>
                    </div>
                    <p v-if="p.status === 'submitted'" class="small text-muted mb-0">{{ $t("marketing.affiliate.payout.submitted_hint") }}</p>
                </card>

                <!-- items -->
                <card class="no-border-card" body-classes="px-0 pb-1">
                    <el-table ref="itemsTable" :data="items" class="aff-table bon-table bon-tight" row-key="id" @expand-change="onExpand" header-row-class-name="thead-light" :empty-text="$t('marketing.affiliate.empty')" data-testid="aff-payout-items">
                        <!-- 1440 px + open sidebar (verifier B): tax id / account name / note
                             live in the expanded row; short columns are fixed width. -->
                        <el-table-column type="expand" width="1" class-name="aff-expand-col">
                            <template v-slot="{ row }">
                                <dl class="row mb-0 px-4 small aff-expand" data-testid="aff-payout-item-expand">
                                    <dt class="col-sm-3">{{ $t("marketing.affiliate.payout.tax_id") }}</dt><dd class="col-sm-9 aff-mono">{{ row.national_id || "-" }}</dd>
                                    <dt class="col-sm-3">{{ $t("marketing.affiliate.profile.account_name") }}</dt><dd class="col-sm-9">{{ row.bank_account_name || "-" }}</dd>
                                    <dt class="col-sm-3">{{ $t("marketing.affiliate.payout.payment_note") }}</dt><dd class="col-sm-9">{{ row.payment_note || "-" }}{{ row.paid_by_name ? " · " + row.paid_by_name : "" }}</dd>
                                    <template v-if="row.below_min">
                                        <dt class="col-sm-3"></dt><dd class="col-sm-9 text-warning">{{ $t("marketing.affiliate.payout.below_min") }}</dd>
                                    </template>
                                </dl>
                            </template>
                        </el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.code')" width="122">
                            <template v-slot="{ row }">
                                <a href="#" class="text-muted mr-1" :aria-expanded="!!expanded[row.id]" :title="$t('marketing.affiliate.payout.details')" data-testid="aff-item-toggle" @click.prevent="toggle(row)">
                                    <i :class="expanded[row.id] ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
                                </a>
                                <span class="aff-mono font-weight-bold">{{ row.code }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.profile.full_name')" min-width="140">
                            <template v-slot="{ row }"><div class="aff-ellipsis" :title="row.full_name">{{ row.full_name }}</div></template>
                        </el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.profile.bank')" width="96"><template v-slot="{ row }">{{ row.bank_name_th || row.bank_code }}</template></el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.profile.account_no')" width="112"><template v-slot="{ row }"><span class="aff-mono">{{ row.bank_account_no }}</span></template></el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.count')" width="96" align="right" prop="commission_count"></el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.gross')" width="96" align="right"><template v-slot="{ row }">{{ money(row.gross) }}</template></el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.wht_short')" width="84" align="right"><template v-slot="{ row }">{{ money(row.wht) }}</template></el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.net')" width="104" align="right"><template v-slot="{ row }"><b>{{ money(row.net) }}</b></template></el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.status')" width="128">
                            <template v-slot="{ row }">
                                <el-tag size="small" :type="row.status === 'paid' ? 'success' : 'info'">{{ tk("marketing.affiliate.payout.item_", row.status) }}</el-tag>
                                <div v-if="row.payment_date" class="small text-muted">{{ row.payment_date }}</div>
                                <el-tooltip v-if="row.below_min" placement="top" :content="$t('marketing.affiliate.payout.below_min')">
                                    <el-tag size="mini" type="warning" class="mt-1" data-testid="aff-below-min">{{ $t("marketing.affiliate.payout.below_min_short") }}</el-tag>
                                </el-tooltip>
                            </template>
                        </el-table-column>
                        <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.actions')" width="72" align="center" fixed="right">
                            <template v-slot="{ row }">
                                <el-dropdown v-if="hasItemAction(row)" trigger="click" @command="(c) => onItem(c, row)">
                                    <base-button size="sm" type="neutral" class="px-2" :aria-label="$t('marketing.affiliate.actions')"><i class="fa fa-ellipsis-v"></i></base-button>
                                    <el-dropdown-menu slot="dropdown">
                                        <el-dropdown-item v-if="canWrite && ia(row).pay" command="pay">{{ $t("marketing.affiliate.payout.pay") }}</el-dropdown-item>
                                        <el-dropdown-item v-if="ia(row).viewSlip" command="slip">{{ $t("marketing.affiliate.payout.view_slip") }}</el-dropdown-item>
                                        <el-dropdown-item v-if="canWrite && ia(row).unpay" command="unpay" divided class="text-danger">{{ $t("marketing.affiliate.payout.unpay") }}</el-dropdown-item>
                                        <el-dropdown-item v-if="canWrite && ia(row).remove" command="remove" class="text-danger">{{ $t("marketing.affiliate.payout.remove_item") }}</el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </template>
                        </el-table-column>
                    </el-table>
                </card>
                <router-link :to="{ path: '/marketing/affiliate/commissions', query: { payout_id: String(p.id) } }" class="small">{{ $t("marketing.affiliate.payout.view_commissions") }} →</router-link>
            </template>
        </div>

        <!-- pay dialog (KOL pattern): transfer date · slip · note -->
        <el-dialog :title="$t('marketing.affiliate.payout.pay')" :visible.sync="payOpen" width="480px" append-to-body :close-on-click-modal="false">
            <div v-if="payRow" data-testid="aff-pay-dialog">
                <p class="mb-3"><b>{{ payRow.code }}</b> · {{ payRow.full_name }} · {{ $t("marketing.affiliate.payout.net") }} ฿{{ money(payRow.net) }}</p>
                <div class="form-group">
                    <label class="form-control-label">{{ $t("marketing.affiliate.payout.payment_date") }} *</label>
                    <el-date-picker v-model="payForm.payment_date" type="date" value-format="yyyy-MM-dd" format="yyyy-MM-dd" class="w-100" data-testid="aff-pay-date"></el-date-picker>
                    <small v-if="payErr.date" class="text-danger">{{ $t("marketing.affiliate.err.required") }}</small>
                </div>
                <div class="form-group">
                    <label class="form-control-label">{{ $t("marketing.affiliate.payout.slip") }} *</label>
                    <input ref="slip" type="file" accept="image/jpeg,image/png,application/pdf" class="form-control-file" data-testid="aff-pay-slip" @change="onSlip" />
                    <small class="d-block text-muted">{{ $t("marketing.affiliate.payout.slip_hint") }}</small>
                    <small v-if="payErr.slip" class="text-danger">{{ $t("marketing.affiliate.err.slip_required") }}</small>
                </div>
                <div class="form-group mb-0">
                    <label class="form-control-label">{{ $t("marketing.affiliate.payout.payment_note") }}</label>
                    <el-input v-model="payForm.payment_note" type="textarea" :rows="2" maxlength="500"></el-input>
                </div>
            </div>
            <span slot="footer">
                <base-button type="secondary" @click="payOpen = false">{{ $t("marketing.affiliate.cancel") }}</base-button>
                <base-button type="success" :loading="busy" data-testid="aff-pay-save" @click="pay">{{ $t("marketing.affiliate.payout.pay") }}</base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { Table, TableColumn, Tag, Tooltip, Alert, Dropdown, DropdownMenu, DropdownItem, Dialog, DatePicker, Input, Loading } from "element-ui";
import { mapGetters } from "vuex";
import { AffiliatePayoutService } from "@/resource";
import { apiErrorCode, isSchemaMissing } from "@/utils/marketing";
import { bkkDate, itemActions, payFormData, payoutActions, payoutStatusTag, saveBlob, slipOk } from "@/utils/affiliate";
import affiliateMixin from "./components/affiliateMixin";

// Affiliate payout round detail (Phase 3 spec 2026-10-13 §5.5).
export default {
    name: "MarketingAffiliatePayoutDetail",
    directives: { loading: Loading.directive },
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Tooltip.name]: Tooltip,
        [Alert.name]: Alert,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        [Dialog.name]: Dialog,
        [DatePicker.name]: DatePicker,
        [Input.name]: Input
    },
    mixins: [affiliateMixin],
    data() {
        return {
            loading: false,
            busy: false,
            stale: false,
            schemaMissing: false,
            p: null,
            items: [],
            payOpen: false,
            payRow: null,
            payForm: { payment_date: "", payment_note: "" },
            payFile: null,
            payErr: {},
            expanded: {}
        };
    },
    computed: {
        ...mapGetters({ canWrite: "Auth/canWriteAffiliateFinance" }),
        acts() {
            return payoutActions(this.p);
        }
    },
    watch: {
        "$route.params.id"() {
            if (this.$route.name === "MarketingAffiliatePayoutDetail") this.load();
        }
    },
    created() {
        this.load();
    },
    methods: {
        statusTag: payoutStatusTag,
        ia(row) {
            return itemActions(this.p, row);
        },
        // Detail row (tax id / account name / note): the expand column is 1 px
        // wide with no arrow, the chevron in the code cell toggles it.
        toggle(row) {
            const t = this.$refs.itemsTable;
            if (t) t.toggleRowExpansion(row);
        },
        onExpand(row, rows) {
            const open = {};
            (rows || []).forEach((r) => (open[r.id] = true));
            this.expanded = open;
        },
        hasItemAction(row) {
            const a = this.ia(row);
            return a.viewSlip || (this.canWrite && (a.pay || a.unpay || a.remove));
        },
        apply(d) {
            if (!d) return;
            this.p = d.payout || null;
            this.items = d.items || [];
            this.stale = false;
        },
        async load() {
            const id = this.$route.params.id;
            this.p = null;
            this.items = [];
            if (!id) return;
            this.loading = true;
            try {
                this.apply(this.unwrap(await AffiliatePayoutService.get(id)));
                this.schemaMissing = false;
            } catch (err) {
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else if (!(err && err.response && err.response.status === 404)) this.showError(err);
            } finally {
                this.loading = false;
            }
        },
        async run(fn) {
            this.busy = true;
            try {
                this.apply(this.unwrap(await fn()));
                this.notifyOk();
                return true;
            } catch (err) {
                if (apiErrorCode(err) === "stale") this.stale = true;
                this.showError(err);
                return false;
            } finally {
                this.busy = false;
            }
        },
        async submit() {
            if (!(await this.confirmBox(this.$t("marketing.affiliate.payout.confirm_submit", { code: this.p.code }), { icon: "question" }))) return;
            await this.run(() => AffiliatePayoutService.submit(this.p.id, { expected_updated_at: this.p.updated_at || null }));
        },
        async cancelRound() {
            const reason = await this.askReason(this.$t("marketing.affiliate.payout.cancel"), { text: this.p.code, danger: true });
            if (reason === null) return;
            await this.run(() => AffiliatePayoutService.cancel(this.p.id, { reason }));
        },
        async csv(kind) {
            try {
                const res = kind === "wht" ? await AffiliatePayoutService.whtCsv(this.p.id) : await AffiliatePayoutService.transferCsv(this.p.id);
                saveBlob(res, `${this.p.code}-${kind === "wht" ? "pnd3" : "transfer"}.csv`);
            } catch (err) {
                this.showError(err);
            }
        },
        async onItem(cmd, row) {
            if (cmd === "pay") {
                this.payRow = row;
                this.payForm = { payment_date: bkkDate(), payment_note: "" };
                this.payFile = null;
                this.payErr = {};
                this.payOpen = true;
                this.$nextTick(() => {
                    if (this.$refs.slip) this.$refs.slip.value = "";
                });
                return;
            }
            if (cmd === "slip") {
                const w = window.open("", "_blank");
                if (w) w.opener = null;
                try {
                    const d = this.unwrap(await AffiliatePayoutService.slipUrl(this.p.id, row.id));
                    if (w && d && d.url) w.location.href = d.url;
                    else if (w) w.close();
                } catch (err) {
                    if (w) w.close();
                    this.showError(err);
                }
                return;
            }
            if (cmd === "unpay") {
                const reason = await this.askReason(this.$t("marketing.affiliate.payout.unpay"), { text: `${row.code} · ${row.full_name}`, danger: true });
                if (reason === null) return;
                await this.run(() => AffiliatePayoutService.unpayItem(this.p.id, row.id, { reason }));
                return;
            }
            if (cmd === "remove") {
                if (!(await this.confirmBox(this.$t("marketing.affiliate.payout.confirm_remove", { code: row.code }), { danger: true }))) return;
                await this.run(() => AffiliatePayoutService.removeItem(this.p.id, row.id));
            }
        },
        onSlip(e) {
            const f = e.target.files && e.target.files[0];
            this.payFile = f && slipOk(f) ? f : null;
            this.payErr = { ...this.payErr, slip: !!f && !this.payFile };
            if (f && !this.payFile) this.$notify({ type: "danger", message: this.$t("marketing.affiliate.payout.slip_hint") });
        },
        async pay() {
            this.payErr = { date: !this.payForm.payment_date, slip: !this.payFile };
            if (this.payErr.date || this.payErr.slip) return;
            const ok = await this.run(() => AffiliatePayoutService.payItem(this.p.id, this.payRow.id, payFormData(this.payForm, this.payFile)));
            if (ok) this.payOpen = false;
        }
    }
};
</script>

<style scoped>
/* Verifier B: English headers broke mid-word (COMMISSIO/N) — the theme breaks
   anywhere; header cells wrap at spaces only. */
.aff-table >>> th .cell {
    word-break: normal;
    overflow-wrap: normal;
}

.aff-mono {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: 0.03em;
}
.aff-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.aff-expand dt {
    font-weight: 600;
}
.mk-aff-payout-detail >>> .aff-expand-col .cell,
.mk-aff-payout-detail >>> .aff-expand-col .el-table__expand-icon {
    display: none;
}
.mk-aff-payout-detail >>> th.aff-expand-col,
.mk-aff-payout-detail >>> td.aff-expand-col {
    padding: 0;
}
</style>
